import type {CSSProperties} from 'react';
import {space} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgePushSiriLook = 'push-prime-enable' | 'push-prime-later' | 'siri-tip-close';

const PUSH_SIRI_LOOKS = new Set<string>(['push-prime-enable', 'push-prime-later', 'siri-tip-close']);
export const isWedgePushSiriLook = (look: string | undefined): look is WedgePushSiriLook => look !== undefined && PUSH_SIRI_LOOKS.has(look);

/* Released push prime card and Siri tip card (native iOS only). The push prime card stacks two full-width blocks in its bottom card:
   Turn on notifications and Not now. Both are 13px radius blocks with no border and 100% width. Turn on notifications has 13px by 14px
   padding, the accent fill, the on-accent ink and 14.5px bold type at line height 1 (one `font` shorthand, as the source writes it), with 8px under it. Not now has 11px by 14px padding,
   no fill, the tertiary label ink and 13.5px semibold type at line height 1. Both set the cursor to pointer and no tap highlight.
   The Siri tip close mark is a bare 14px icon button: no border, no fill, 4px padding, a 2px lift (negative top margin) and the pointer
   cursor. Its ink is the quaternary label by default. The card passes its own ink as the color of its style when the tip sits over the
   fixed dark Drive Mode surface, so the look never builds a color string.
   The block looks read the exact legacy roles and keep the declaration order of the source, so the shipped ink rules keep matching the
   serialized text. The on-accent ink is the bare variable: the Wedge scope always defines it, so the source fallback is never read.
   All three keep the default transparent 44px target and its anchor (wedgeSettingsDrawsHit). The blocks are 40.5px and 35.5px tall with
   8px between them, so the two regions reach 1.75px and 4.25px into the gap and leave 2px: no overlap. A browser check with the anchor on
   drew the same pixels as legacy (0 raster differences against 0 of same-side noise). The close mark is alone at the end of the card row. */

export function wedgePushSiriStyle(look: WedgePushSiriLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'push-prime-enable': return {
      width: '100%', padding: '13px 14px', borderRadius: 13, border: 0, background: r('accent'), color: r('onAccent'),
      font: `700 14.5px/1 ${r('font')}`, cursor: 'pointer', marginBottom: space[2],
      WebkitTapHighlightColor: 'transparent',
    };
    case 'push-prime-later': return {
      width: '100%', padding: '11px 14px', borderRadius: 13, border: 0, background: 'transparent', color: r('labelTer'),
      font: `600 13.5px/1 ${r('font')}`, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
    };
    case 'siri-tip-close': return {
      border: 0, background: 'transparent', color: r('labelQuad'), cursor: 'pointer', padding: space[1], marginTop: -2,
      WebkitTapHighlightColor: 'transparent',
    };
  }
}
